import React, { useState, useEffect } from "react";
import { SimpleModulePage } from "./SimpleModulePage";
import { HealthMetricsModal, HealthMetricType, HealthRecord, AverageMetrics } from "../components/HealthMetricsModal";
import * as healthService from "../services/healthMetricsService";

// ===== 날짜 포맷팅 함수 =====
const formatDate = (dateString: string): string => {
  if (!dateString) return "날짜 없음";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "날짜 오류";
    return date.toLocaleDateString("ko-KR", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    console.error("[formatDate] Error:", error, dateString);
    return "날짜 오류";
  }
};

// ===== 공통 카드 래퍼 =====
const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>{children}</div>
);

interface MeasurePageProps {
  onBack: () => void;
}

export const MeasurePage: React.FC<MeasurePageProps> = ({ onBack }) => {
  const [activeModal, setActiveModal] = useState<HealthMetricType | null>(null);
  const [bpLatest, setBpLatest] = useState<HealthRecord | null>(null);
  const [glucoseLatest, setGlucoseLatest] = useState<HealthRecord | null>(null);
  const [bpAverage, setBpAverage] = useState<AverageMetrics>({});
  const [glucoseAverage, setGlucoseAverage] = useState<AverageMetrics>({});
  const [bpHistory, setBpHistory] = useState<HealthRecord[]>([]);
  const [glucoseHistory, setGlucoseHistory] = useState<HealthRecord[]>([]);
  const [bpOffset, setBpOffset] = useState(0);
  const [glucoseOffset, setGlucoseOffset] = useState(0);
  const [bpHasMore, setBpHasMore] = useState(true);
  const [glucoseHasMore, setGlucoseHasMore] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 페이지 진입 시 스크롤 상단으로 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadHealthData();
  }, []);

  const loadHealthData = async () => {
    try {
      setIsLoading(true);
      
      // 토큰 확인
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      if (!token) {
        console.warn("[MeasurePage] No auth token found, skipping health data load");
        setBpLatest(null);
        setGlucoseLatest(null);
        setBpAverage({});
        setGlucoseAverage({});
        setBpHistory([]);
        setGlucoseHistory([]);
        setIsLoading(false);
        return;
      }
      
      const [bp, glucose, bpAvg, glucoseAvg, bpHistoryResult, glucoseHistoryResult] = await Promise.all([
        healthService.getLatestBloodPressure().catch((err) => {
          console.error("Failed to load latest BP:", err);
          return null;
        }),
        healthService.getLatestBloodGlucose().catch((err) => {
          console.error("Failed to load latest glucose:", err);
          return null;
        }),
        healthService.getBloodPressureAverage().catch((err) => {
          console.error("Failed to load BP average:", err);
          return {};
        }),
        healthService.getBloodGlucoseAverage().catch((err) => {
          console.error("Failed to load glucose average:", err);
          return {};
        }),
        healthService.getBloodPressureHistoryPaginated(20, 0).catch((err) => {
          console.error("Failed to load BP history:", err);
          return { data: [], hasMore: false };
        }),
        healthService.getBloodGlucoseHistoryPaginated(20, 0).catch((err) => {
          console.error("Failed to load glucose history:", err);
          return { data: [], hasMore: false };
        }),
      ]);

      setBpLatest(bp);
      setGlucoseLatest(glucose);
      setBpAverage(bpAvg || {});
      setGlucoseAverage(glucoseAvg || {});
      setBpHistory(bpHistoryResult?.data || []);
      setGlucoseHistory(glucoseHistoryResult?.data || []);
      setBpHasMore(bpHistoryResult?.hasMore || false);
      setGlucoseHasMore(glucoseHistoryResult?.hasMore || false);
      setBpOffset(20);
      setGlucoseOffset(20);

      if (process.env.NODE_ENV === "development") {
        console.log("Health data loaded:", { bp, glucose, bpAvg, glucoseAvg, bpHistoryResult, glucoseHistoryResult });
      }
    } catch (error) {
      console.error("Failed to load health data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleHealthModalOpen = (type: HealthMetricType) => {
    console.log('[MeasurePage] handleHealthModalOpen called with type:', type);
    setActiveModal(type);
  };

  const handleHealthModalClose = () => {
    setActiveModal(null);
    loadHealthData();
  };

  const handleHistoryScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isNearBottom = element.scrollHeight - (element.scrollTop + element.clientHeight) < 100;

    if (isNearBottom && !historyLoading) {
      try {
        setHistoryLoading(true);

        // 혈압과 혈당 데이터를 동시에 로드
        const [newBpData, newGlucoseData] = await Promise.all([
          bpHasMore
            ? healthService
                .getBloodPressureHistoryPaginated(20, bpOffset)
                .catch(() => ({ data: [], hasMore: false }))
            : Promise.resolve({ data: [], hasMore: false }),
          glucoseHasMore
            ? healthService
                .getBloodGlucoseHistoryPaginated(20, glucoseOffset)
                .catch(() => ({ data: [], hasMore: false }))
            : Promise.resolve({ data: [], hasMore: false }),
        ]);

        // 새로운 데이터 추가
        if (newBpData.data.length > 0) {
          setBpHistory((prev) => [...prev, ...newBpData.data]);
          setBpOffset((prev) => prev + 20);
          setBpHasMore(newBpData.hasMore);
        }

        if (newGlucoseData.data.length > 0) {
          setGlucoseHistory((prev) => [...prev, ...newGlucoseData.data]);
          setGlucoseOffset((prev) => prev + 20);
          setGlucoseHasMore(newGlucoseData.hasMore);
        }
      } catch (error) {
        console.error("Failed to load more history:", error);
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  const handleHealthSubmit = async (data: any): Promise<void> => {
    try {
      if (activeModal === "blood_pressure") {
        await healthService.addBloodPressure(data);
      } else if (activeModal === "blood_glucose") {
        await healthService.addBloodGlucose(data);
      }
      await loadHealthData();
    } catch (error) {
      throw error;
    }
  };

  return (
    <SimpleModulePage title="측정" subtitle="(혈당/혈압)" icon="🩺" onBack={onBack}>
      {/* 혈압 카드 */}
      <SectionCard>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[12px] font-semibold text-slate-900">혈압</p>
          <button
            onClick={() => handleHealthModalOpen("blood_pressure")}
            className="text-[11px] px-2 py-1 rounded bg-violet-50 text-violet-600 hover:bg-violet-100 font-medium transition-colors"
          >
            기록하기
          </button>
        </div>

        {bpLatest ? (
          <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] text-slate-600 font-semibold">최근 기록</p>
              <p className="text-[10px] text-slate-500">{formatDate((bpLatest as any).measurement_time)}</p>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-[24px] font-bold text-violet-700">{bpLatest.systolic}</span>
              <span className="text-[18px] text-slate-400">/</span>
              <span className="text-[24px] font-bold text-violet-700">{bpLatest.diastolic}</span>
              <span className="text-[12px] text-slate-600 ml-2">mmHg</span>
              {bpLatest.pulse && (
                <span className="text-[12px] text-slate-600 ml-auto">
                  맥박: {bpLatest.pulse} bpm
                </span>
              )}
            </div>
          </div>
        ) : null}

        {(bpAverage.week || bpAverage.month || bpAverage.threeMonth) && (
          <div className="grid grid-cols-3 gap-2">
            {bpAverage.week && (
              <div className="text-center p-2 bg-slate-50 rounded border border-slate-100">
                <p className="text-[10px] text-slate-600 mb-1 font-semibold">1주일</p>
                <p className="text-[12px] font-bold text-slate-900">
                  {bpAverage.week.systolic?.toFixed(0)}/{bpAverage.week.diastolic?.toFixed(0)}
                </p>
              </div>
            )}
            {bpAverage.month && (
              <div className="text-center p-2 bg-slate-50 rounded border border-slate-100">
                <p className="text-[10px] text-slate-600 mb-1 font-semibold">1개월</p>
                <p className="text-[12px] font-bold text-slate-900">
                  {bpAverage.month.systolic?.toFixed(0)}/{bpAverage.month.diastolic?.toFixed(0)}
                </p>
              </div>
            )}
            {bpAverage.threeMonth && (
              <div className="text-center p-2 bg-slate-50 rounded border border-slate-100">
                <p className="text-[10px] text-slate-600 mb-1 font-semibold">3개월</p>
                <p className="text-[12px] font-bold text-slate-900">
                  {bpAverage.threeMonth.systolic?.toFixed(0)}/{bpAverage.threeMonth.diastolic?.toFixed(0)}
                </p>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* 혈당 카드 */}
      <SectionCard>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[12px] font-semibold text-slate-900">혈당</p>
          <button
            onClick={() => handleHealthModalOpen("blood_glucose")}
            className="text-[11px] px-2 py-1 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 font-medium transition-colors"
          >
            기록하기
          </button>
        </div>

        {glucoseLatest ? (
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] text-slate-600 font-semibold">최근 기록</p>
              <p className="text-[10px] text-slate-500">{formatDate((glucoseLatest as any).measurement_time)}</p>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-[24px] font-bold text-blue-700">{glucoseLatest.glucose_level}</span>
              <span className="text-[12px] text-slate-600">mg/dL</span>
              <span className="text-[10px] text-slate-500 ml-auto">
                {glucoseLatest.meal_relation === "fasting" && "공복"}
                {glucoseLatest.meal_relation === "before_meal" && "식전"}
                {glucoseLatest.meal_relation === "after_meal" && "식후"}
                {glucoseLatest.meal_relation === "bedtime" && "자기 전"}
                {glucoseLatest.meal_relation === "general" && "일반"}
              </span>
            </div>
          </div>
        ) : null}

        {(glucoseAverage.week || glucoseAverage.month || glucoseAverage.threeMonth) && (
          <div className="grid grid-cols-3 gap-2">
            {glucoseAverage.week && (
              <div className="text-center p-2 bg-slate-50 rounded border border-slate-100">
                <p className="text-[10px] text-slate-600 mb-1 font-semibold">1주일</p>
                <p className="text-[12px] font-bold text-slate-900">
                  {glucoseAverage.week.glucose_level?.toFixed(1)}
                </p>
              </div>
            )}
            {glucoseAverage.month && (
              <div className="text-center p-2 bg-slate-50 rounded border border-slate-100">
                <p className="text-[10px] text-slate-600 mb-1 font-semibold">1개월</p>
                <p className="text-[12px] font-bold text-slate-900">
                  {glucoseAverage.month.glucose_level?.toFixed(1)}
                </p>
              </div>
            )}
            {glucoseAverage.threeMonth && (
              <div className="text-center p-2 bg-slate-50 rounded border border-slate-100">
                <p className="text-[10px] text-slate-600 mb-1 font-semibold">3개월</p>
                <p className="text-[12px] font-bold text-slate-900">
                  {glucoseAverage.threeMonth.glucose_level?.toFixed(1)}
                </p>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* 기록 히스토리 */}
      <SectionCard>
        <p className="text-[12px] font-semibold text-slate-900 mb-3">기록 히스토리</p>
        <div className="space-y-2 max-h-80 overflow-y-auto" onScroll={handleHistoryScroll}>
          {bpHistory.length === 0 && glucoseHistory.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-[12px] text-slate-500">기록된 데이타가 없습니다</p>
            </div>
          ) : (
            <>
              {[...bpHistory, ...glucoseHistory]
                .sort((a, b) => new Date(b.measurement_time).getTime() - new Date(a.measurement_time).getTime())
                .map((record, idx) => {
                  const date = new Date(record.measurement_time);
                  const formattedDate = date.toLocaleDateString("ko-KR", {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                  const isBPRecord = record.systolic !== undefined;
                  return (
                    <div key={idx} className="flex items-center justify-between text-[11px] p-2 bg-slate-50 rounded border border-slate-100 hover:bg-slate-100 transition-colors">
                      <div className="flex-1">
                        {isBPRecord ? (
                          <>
                            <span className="inline-block px-1.5 py-0.5 bg-violet-100 text-violet-700 text-[9px] font-semibold rounded mr-2">혈압</span>
                            <span className="text-slate-900 font-semibold">{record.systolic} / {record.diastolic} mmHg {record.pulse && `• ${record.pulse} bpm`}</span>
                          </>
                        ) : (
                          <>
                            <span className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[9px] font-semibold rounded mr-2">혈당</span>
                            <span className="text-slate-900 font-semibold">{record.glucose_level} mg/dL</span>
                          </>
                        )}
                      </div>
                      <span className="text-slate-500 ml-2 whitespace-nowrap text-[10px]">{formattedDate}</span>
                    </div>
                  );
                })}
              {historyLoading && (
                <div className="flex items-center justify-center py-4">
                  <p className="text-[12px] text-slate-500">더보기...</p>
                </div>
              )}
              {!bpHasMore && !glucoseHasMore && bpHistory.length > 0 && glucoseHistory.length > 0 && (
                <div className="flex items-center justify-center py-4">
                  <p className="text-[12px] text-slate-400">모든 기록을 조회했습니다</p>
                </div>
              )}
            </>
          )}
        </div>
      </SectionCard>

      {/* 혈압 모달 */}
      {(() => {
        console.log('[MeasurePage] Rendering BP Modal with bpLatest:', bpLatest);
        return null;
      })()}
      <HealthMetricsModal
        isOpen={activeModal === "blood_pressure"}
        onClose={handleHealthModalClose}
        metricType="blood_pressure"
        latestRecord={bpLatest || undefined}
        averageMetrics={bpAverage}
        history={[...bpHistory, ...glucoseHistory]}
        onSubmit={handleHealthSubmit}
        isLoading={isLoading}
      />

      {/* 혈당 모달 */}
      {(() => {
        console.log('[MeasurePage] Rendering Glucose Modal with glucoseLatest:', glucoseLatest);
        return null;
      })()}
      <HealthMetricsModal
        isOpen={activeModal === "blood_glucose"}
        onClose={handleHealthModalClose}
        metricType="blood_glucose"
        latestRecord={glucoseLatest || undefined}
        averageMetrics={glucoseAverage}
        history={[...bpHistory, ...glucoseHistory]}
        onSubmit={handleHealthSubmit}
        isLoading={isLoading}
      />
    </SimpleModulePage>
  );
};
